import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Rating,
  Chip,
  Button,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ShoppingCart as CartIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { useConfirmation } from '../hooks/useConfirmation';
import ConfirmationModal from '../components/ConfirmationModal';
import { confirmations } from '../utils/confirmation';

const Favorites: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { state: favoritesState, removeFavorite, clearFavorites } = useFavorites();
  const { addItem, isInCart } = useCart();
  const { t } = useLanguage();
  const [viewMode] = useState<'grid' | 'list'>('grid');
  const { confirmation, showConfirmation } = useConfirmation();

  // Format price
  const formatPrice = (price: number) => {
    return price.toLocaleString() + ' FCFA';
  };

  // Handle add to cart
  const handleAddToCart = (product: any) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      stock: product.stock,
    });
  };

  // Handle remove from favorites
  const handleRemoveFavorite = (productId: string) => {
    removeFavorite(productId);
  };

  // Handle clear all favorites
  const handleClearFavorites = () => {
    showConfirmation(
      confirmations.clear('les favoris'),
      () => clearFavorites()
    );
  };

  if (favoritesState.favorites.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center' }}>
          <FavoriteBorderIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            {t.favorites.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {t.favorites.emptyDescription}
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={RouterLink}
            to="/products"
            startIcon={<ArrowBackIcon />}
          >
            {t.favorites.discoverProducts}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {t.favorites.title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {favoritesState.itemCount} produit(s) dans vos favoris
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleClearFavorites}
              size={isMobile ? "small" : "medium"}
            >
              {t.favorites.clearFavorites}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Favorites Grid/List */}
      <Grid container spacing={3}>
        {favoritesState.favorites.map((product) => (
          <Grid 
            item 
            xs={12} 
            sm={viewMode === 'grid' ? 6 : 12} 
            lg={viewMode === 'grid' ? 4 : 12} 
            xl={viewMode === 'grid' ? 3 : 12} 
            key={product.id}
          >
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: viewMode === 'list' ? 'row' : 'column',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {/* Remove from favorites button */}
              <IconButton
                onClick={() => handleRemoveFavorite(product.id)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' },
                  zIndex: 1,
                }}
                size="small"
              >
                <FavoriteIcon color="error" fontSize="small" />
              </IconButton>

              <CardMedia
                component="img"
                sx={{ 
                  width: viewMode === 'list' ? 200 : '100%',
                  height: viewMode === 'list' ? 200 : 200,
                  objectFit: 'cover',
                  flexShrink: 0
                }}
                image={product.image}
                alt={product.name}
              />
              <CardContent sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column',
                p: { xs: 2, sm: 3 }
              }}>
                <Typography variant="h6" component="h3" sx={{ 
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  lineHeight: 1.2,
                  mb: 1
                }}>
                  {product.name}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Rating value={product.rating} precision={0.1} size="small" readOnly />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({product.reviews})
                  </Typography>
                </Box>
                
                <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                  {formatPrice(product.price)}
                </Typography>
                
                {viewMode === 'list' && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      flex: 1
                    }}
                  >
                    Produit de construction de qualité
                  </Typography>
                )}
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {product.isNew && (
                    <Chip label="Nouveau" color="secondary" size="small" />
                  )}
                  {product.isFeatured && (
                    <Chip label="Populaire" color="primary" size="small" />
                  )}
                  {product.stock < 50 && (
                    <Chip label="Stock limité" color="warning" size="small" />
                  )}
                </Box>
                
                <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<CartIcon />}
                    onClick={() => handleAddToCart(product)}
                    disabled={isInCart(product.id) || product.stock === 0}
                    size="small"
                    sx={{ flex: viewMode === 'list' ? 1 : 'none' }}
                  >
                    {isInCart(product.id) ? t.favorites.inCart : t.favorites.addToCart}
                  </Button>
                  {viewMode === 'list' && (
                    <Button
                      variant="outlined"
                      size="small"
                      component={RouterLink}
                      to={`/product/${product.id}`}
                      startIcon={<ViewIcon />}
                    >
                      {t.favorites.viewDetails}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Empty state after clearing */}
      {favoritesState.favorites.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 4 }}>
          <FavoriteBorderIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t.favorites.empty}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t.favorites.emptyDescription}
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/products"
            startIcon={<ArrowBackIcon />}
          >
            {t.favorites.discoverProducts}
          </Button>
        </Paper>
      )}

      {/* Confirmation Modal */}
      {confirmation && (
        <ConfirmationModal
          open={confirmation.open}
          title={confirmation.title}
          message={confirmation.message}
          type={confirmation.type}
          confirmText={confirmation.confirmText}
          cancelText={confirmation.cancelText}
          onConfirm={confirmation.onConfirm}
          onCancel={confirmation.onCancel}
          confirmButtonColor={confirmation.confirmButtonColor}
        />
      )}
    </Container>
  );
};

export default Favorites; 